import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Clock,
  IndianRupee,
  Loader2,
  MessageCircle,
  Phone,
  Sparkles,
  TrendingUp,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { BRAND } from "@/lib/site";
import { setLandingPersona, getAttribution } from "@/lib/utm";

const fmtINR = (n) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Math.round(n || 0));
const fmtINRShort = (n) => {
  const v = Math.round(n || 0);
  if (v >= 1_00_00_000) return `₹${(v / 1_00_00_000).toFixed(2)} Cr`;
  if (v >= 1_00_000) return `₹${(v / 1_00_000).toFixed(2)} L`;
  return `₹${v}`;
};

const TERMS = [
  { v: 30, l: "30 days (Net-30)" },
  { v: 45, l: "45 days (Net-45)" },
  { v: 60, l: "60 days (Net-60)" },
  { v: 90, l: "90 days (Net-90)" },
  { v: 120, l: "120 days (Net-120)" },
];

const FAQ = [
  {
    q: "Is invoice discounting a loan?",
    a: "No. It's a sale of your receivable to a financier at a discount. Your balance sheet doesn't carry it as debt — which means it doesn't affect your CIBIL or future borrowing capacity. Many GCC vendors prefer this exact reason.",
  },
  {
    q: "How quickly can I get the money?",
    a: "Typically 24–48 hours from invoice approval. The first cycle takes longer (3–5 working days for lender on-boarding + buyer/anchor verification); every subsequent invoice is near-real-time on platform-led programs.",
  },
  {
    q: "What does it cost?",
    a: "Indicative range is 1.0–1.8% per month on the advance, depending on your buyer's credit rating, your invoice ageing track record and the lender. We negotiate this for you and run the math against your gross margin so it stays accretive.",
  },
  {
    q: "Will my GCC client / anchor know?",
    a: "Two structures exist — disclosed (anchor-acknowledged) and undisclosed (vendor-only). Most large GCCs and System Integrators are happy with disclosed programs and even have empanelled financiers. We'll choose the route that fits your relationship.",
  },
  {
    q: "Minimum invoice size?",
    a: "Most lenders we work with accept invoices from ₹2 Lakh upwards, with a programme floor of ₹25 Lakh per month in eligible receivables. Below that, we package channel finance or working capital instead.",
  },
  {
    q: "What documents do I need?",
    a: "GST returns (12 months), bank statement (12 months), GSTIN, MSME/Udyam, KYC of promoters, sample 2–3 invoices and the buyer's PO/MSA. We share a single checklist; no chasing.",
  },
];

export default function InvoiceFinancingPage() {
  // SEO + persona attribution
  useEffect(() => {
    const prevTitle = document.title;
    const prevDesc = document.querySelector("meta[name='description']")?.getAttribute("content");
    document.title = "Invoice Financing for IT & Tech Vendors in Bangalore | Swift Financial";
    const setMeta = (name, content) => {
      let m = document.querySelector(`meta[name='${name}']`);
      if (!m) {
        m = document.createElement("meta");
        m.setAttribute("name", name);
        document.head.appendChild(m);
      }
      m.setAttribute("content", content);
    };
    setMeta(
      "description",
      "Specialist invoice discounting & receivables financing for Tier-2 IT and tech service vendors in Bangalore — supplying GCCs, large SIs and IT parks. Unlock up to 90% of your invoice value in 24–48 hours."
    );
    setLandingPersona("it-vendor-invoice-financing");
    return () => {
      document.title = prevTitle;
      if (prevDesc) document.querySelector("meta[name='description']")?.setAttribute("content", prevDesc);
    };
  }, []);

  // Calculator state
  const [billing, setBilling] = useState(20_00_000); // ₹20L/mo
  const [terms, setTerms] = useState(60);
  const [advance, setAdvance] = useState(85); // %
  const [feePerMonth, setFeePerMonth] = useState(1.5); // % per month

  const calc = useMemo(() => {
    const outstanding = billing * (terms / 30);
    const unlocked = outstanding * (advance / 100);
    const cyclesPerYear = 12; // monthly billing
    const feeForCycle = unlocked * (feePerMonth / 100) * (terms / 30);
    const annualFee = feeForCycle * cyclesPerYear;
    const effectiveAnnualRate = (feePerMonth / 100) * 12 * 100;
    const daysSaved = Math.max(0, terms - 2);
    return { outstanding, unlocked, feeForCycle, annualFee, effectiveAnnualRate, daysSaved };
  }, [billing, terms, advance, feePerMonth]);

  // Lead form
  const [form, setForm] = useState({ name: "", phone: "", email: "", company: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Please fill name, phone and email.");
      return;
    }
    setSubmitting(true);
    try {
      const attr = getAttribution();
      const message = [
        `IT Vendor — Invoice Financing inquiry`,
        form.company ? `Company: ${form.company}` : null,
        `Indicative monthly billing: ${fmtINRShort(billing)}`,
        `Buyer payment terms: ${terms} days`,
        `Targeted advance: ${advance}%`,
        `Working capital unlocked (illustrative): ${fmtINRShort(calc.unlocked)}`,
      ]
        .filter(Boolean)
        .join(" | ");
      await api.post("/inquiries", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        loan_type: "business_loan",
        employment_type: "business_owner",
        loan_amount: fmtINRShort(calc.unlocked),
        city: "Bangalore",
        message,
        persona: "it-vendor-invoice-financing",
        landing_page: window.location.pathname,
        utm_source: attr.utm_source,
        utm_medium: attr.utm_medium,
        utm_campaign: attr.utm_campaign,
        utm_content: attr.utm_content,
        utm_term: attr.utm_term,
        referrer: attr.referrer,
      });
      setDone(true);
      toast.success("Got it — Raj will reach out shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="invoice-financing-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-brand-forest text-brand-cream isolate">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1600&q=80"
            alt="Bangalore IT corridor"
            className="h-full w-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-brand-forest/95 via-brand-forest/85 to-brand-forest/55" />
          <div className="absolute inset-0 bg-grain opacity-20" />
        </div>

        <div className="container-x relative z-10 py-20 md:py-28">
          <div className="max-w-3xl animate-fade-up">
            <span className="eyebrow text-brand-gold">Specialist Programme · Bangalore</span>
            <h1 className="mt-4 font-outfit font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Invoice Financing for IT &amp; Tech Vendors in <span className="text-brand-gold italic font-medium">Bangalore</span>.
            </h1>
            <p className="mt-6 text-lg text-brand-cream/80 leading-relaxed max-w-2xl">
              You bill GCCs, System Integrators or IT parks across Electronics City,
              Whitefield or Koramangala — and wait 60, 90, even 120 days for the payment
              to land. We unlock <span className="text-brand-gold font-semibold">up to 90% of that invoice in 24–48 hours</span>,
              so payroll, raw-material and the next contract don't get held hostage by your buyer's payment cycle.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a href="#calculator" data-testid="if-cta-calc" className="btn-primary text-base">
                Run my numbers <ArrowRight className="h-4 w-4" />
              </a>
              <a
                data-testid="if-cta-whatsapp"
                href={`https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent("Hi Raj, I'm an IT vendor looking at invoice financing for my GCC receivables.")}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-brand-gold/40 bg-brand-cream/5 px-6 py-3 text-sm font-semibold text-brand-cream backdrop-blur-md hover:bg-brand-cream/10"
              >
                <MessageCircle className="h-4 w-4" /> Talk to Raj
              </a>
            </div>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl">
              {[
                { v: "90%", l: "Of invoice value" },
                { v: "24-48h", l: "To disbursal" },
                { v: "0", l: "Added to balance sheet" },
                { v: "700+", l: "GCCs in Bangalore (2025)" },
              ].map((s, i) => (
                <div key={i} className="rounded-2xl border border-brand-cream/15 bg-brand-cream/5 backdrop-blur-md px-4 py-4">
                  <div className="font-outfit font-extrabold text-2xl sm:text-3xl text-brand-gold">{s.v}</div>
                  <div className="mt-1 text-xs uppercase tracking-[0.16em] text-brand-cream/65">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CONTEXT WRITE-UP */}
      <section className="container-x py-20 md:py-24">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <span className="eyebrow">Why this exists</span>
            <h2 className="mt-4 h-display text-3xl sm:text-4xl">
              The cash-flow gap nobody at the bank wants to fix.
            </h2>
            <div className="mt-6 space-y-4 text-brand-forest/85 leading-relaxed">
              <p>
                Bangalore now hosts <strong>700+ Global Capability Centres</strong>, with new GCCs and
                expansion mandates landing through 2025–26 in AI, cybersecurity and enterprise software.
                Every one of them is supplied by a long tail of Tier-2 IT and tech service vendors — and
                every one of those vendors waits 60, 90, sometimes 120 days for an invoice to clear.
              </p>
              <p>
                Meanwhile, salaries, server costs, sub-contractor payouts and the next pursuit
                don't pause. Banks underwrite this segment slowly. Generic DSAs don't understand
                the buyer-anchor mechanics. Big platforms target ₹50 Cr+ enterprise programs.
              </p>
              <p>
                <strong>The ₹5–₹50 Lakh invoice bracket — typical of small IT vendors — is the gap.</strong>{" "}
                That's where Swift Financial steps in: programme structuring, lender selection,
                pricing negotiation and ongoing operations, all on your side of the table.
              </p>
            </div>
          </div>

          <aside className="md:col-span-5">
            <div className="card-soft p-7">
              <Sparkles className="h-6 w-6 text-brand-terracotta" />
              <h3 className="mt-4 font-outfit font-bold text-xl text-brand-forest">
                Why Swift Financial fits IT vendors specifically
              </h3>
              <ul className="mt-5 space-y-3 text-sm text-brand-forest/85">
                <li className="flex gap-3"><span className="text-brand-terracotta font-bold">—</span> We understand GCC and SI procurement cycles, not just KYC documents.</li>
                <li className="flex gap-3"><span className="text-brand-terracotta font-bold">—</span> Disclosed and undisclosed programmes — chosen based on your buyer relationship.</li>
                <li className="flex gap-3"><span className="text-brand-terracotta font-bold">—</span> Direct access to invoice-discounting NBFCs, bank programmes and platforms.</li>
                <li className="flex gap-3"><span className="text-brand-terracotta font-bold">—</span> Recurring relationship — every billing cycle, not just one transaction.</li>
                <li className="flex gap-3"><span className="text-brand-terracotta font-bold">—</span> Bangalore-local — face-to-face support across Electronics City, Whitefield, Koramangala.</li>
              </ul>
            </div>
          </aside>
        </div>
      </section>

      {/* CALCULATOR */}
      <section id="calculator" className="bg-brand-sand/40 py-20 md:py-24 scroll-mt-28">
        <div className="container-x">
          <div className="max-w-2xl">
            <span className="eyebrow">Working-Capital Unlock Calculator</span>
            <h2 className="mt-4 h-display text-3xl sm:text-4xl">
              See exactly how much cash is locked inside your unpaid invoices.
            </h2>
            <p className="mt-4 text-brand-sage leading-relaxed">
              Enter your monthly billing and your buyer's payment terms — we'll show
              what's recoverable in 24–48 hours, the indicative cost, and the days
              you'd save per cycle.
            </p>
          </div>

          <div className="mt-10 card-soft p-6 md:p-8" data-testid="if-calculator">
            <div className="grid lg:grid-cols-2 gap-10 items-start">
              {/* INPUTS */}
              <div className="space-y-7">
                {/* Monthly billing */}
                <div data-testid="if-billing-field">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium text-brand-forest">Monthly billing to GCC / SI clients</Label>
                    <div className="relative">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2"><IndianRupee className="h-4 w-4 text-brand-sage" /></span>
                      <Input
                        value={fmtINR(billing)}
                        onChange={(e) => {
                          const v = parseInt(e.target.value.replace(/[^\d]/g, ""), 10);
                          if (!isNaN(v)) setBilling(Math.min(2_00_00_000, Math.max(2_00_000, v)));
                        }}
                        className="h-9 w-40 pl-7 text-right font-outfit font-semibold text-brand-forest"
                        data-testid="if-billing-input"
                      />
                    </div>
                  </div>
                  <Slider
                    className="mt-4"
                    value={[billing]}
                    min={2_00_000}
                    max={2_00_00_000}
                    step={50_000}
                    onValueChange={(v) => setBilling(v[0])}
                    data-testid="if-billing-slider"
                  />
                  <div className="mt-2 flex items-center justify-between text-[11px] text-brand-sage">
                    <span>₹2 L</span>
                    <span className="font-semibold text-brand-terracotta">{fmtINRShort(billing)}/month</span>
                    <span>₹2 Cr</span>
                  </div>
                </div>

                {/* Payment terms */}
                <div data-testid="if-terms-field">
                  <Label className="text-sm font-medium text-brand-forest">Buyer payment terms</Label>
                  <Select value={String(terms)} onValueChange={(v) => setTerms(parseInt(v, 10))}>
                    <SelectTrigger data-testid="if-terms-select" className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TERMS.map((t) => (
                        <SelectItem key={t.v} value={String(t.v)}>{t.l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Advance % */}
                <div data-testid="if-advance-field">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium text-brand-forest">Targeted advance %</Label>
                    <span className="font-outfit font-semibold text-brand-forest">{advance}%</span>
                  </div>
                  <Slider
                    className="mt-4"
                    value={[advance]}
                    min={60}
                    max={90}
                    step={1}
                    onValueChange={(v) => setAdvance(v[0])}
                    data-testid="if-advance-slider"
                  />
                  <div className="mt-2 text-[11px] text-brand-sage">
                    Most lender programmes offer 80–90% on AAA-rated buyers, 60–80% on emerging anchors.
                  </div>
                </div>

                {/* Fee */}
                <div data-testid="if-fee-field">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium text-brand-forest">Indicative discount fee</Label>
                    <span className="font-outfit font-semibold text-brand-forest">{feePerMonth.toFixed(2)}% / month</span>
                  </div>
                  <Slider
                    className="mt-4"
                    value={[feePerMonth * 100]}
                    min={100}
                    max={250}
                    step={5}
                    onValueChange={(v) => setFeePerMonth(v[0] / 100)}
                    data-testid="if-fee-slider"
                  />
                  <div className="mt-2 text-[11px] text-brand-sage">
                    Typical range 1.0–1.8% per month, depending on buyer rating and invoice age.
                  </div>
                </div>
              </div>

              {/* RESULTS */}
              <div className="rounded-2xl bg-brand-forest text-brand-cream p-7 relative overflow-hidden">
                <div className="absolute -top-16 -right-16 h-44 w-44 rounded-full bg-brand-terracotta/25 blur-2xl" />
                <div className="absolute -bottom-16 -left-16 h-44 w-44 rounded-full bg-brand-gold/20 blur-2xl" />

                <div className="relative">
                  <div className="text-xs uppercase tracking-[0.18em] text-brand-gold">Working capital you can unlock today</div>
                  <div data-testid="if-result-unlocked" className="mt-2 font-outfit font-extrabold text-4xl sm:text-5xl tracking-tight">
                    {fmtINRShort(calc.unlocked)}
                  </div>
                  <div className="mt-1 text-sm text-brand-cream/70">
                    on ~{fmtINRShort(calc.outstanding)} of outstanding receivables
                  </div>

                  <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
                    <ResultBox
                      icon={<Clock className="h-4 w-4" />}
                      label="Days saved per cycle"
                      value={`${calc.daysSaved} days`}
                      testid="if-result-days"
                    />
                    <ResultBox
                      icon={<TrendingUp className="h-4 w-4" />}
                      label="Effective annualised cost"
                      value={`~${calc.effectiveAnnualRate.toFixed(1)}% p.a.`}
                      testid="if-result-rate"
                    />
                    <ResultBox
                      icon={<IndianRupee className="h-4 w-4" />}
                      label="Indicative fee per cycle"
                      value={fmtINRShort(calc.feeForCycle)}
                      testid="if-result-fee-cycle"
                    />
                    <ResultBox
                      icon={<Zap className="h-4 w-4" />}
                      label="Annualised fee outlay"
                      value={fmtINRShort(calc.annualFee)}
                      testid="if-result-fee-annual"
                    />
                  </div>

                  <a
                    href="#lead"
                    data-testid="if-results-cta"
                    className="mt-7 inline-flex items-center justify-center gap-2 w-full rounded-full bg-brand-terracotta px-5 py-3 text-sm font-semibold text-white hover:bg-[#b25535] hover:-translate-y-0.5 transition-all"
                  >
                    Get a tailored programme <ArrowRight className="h-4 w-4" />
                  </a>
                  <p className="mt-3 text-[11px] text-brand-cream/55 text-center">
                    Indicative only — actual advance %, fee and turnaround depend on buyer credit rating, invoice ageing track record and lender approval.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="container-x py-20 md:py-24">
        <div className="max-w-2xl">
          <span className="eyebrow">How a typical engagement runs</span>
          <h2 className="mt-4 h-display text-3xl sm:text-4xl">From dormant receivable to cash in 5 working days.</h2>
        </div>
        <ol className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { n: "01", t: "Discovery + buyer mapping", d: "Quick call. We map your top 3–5 anchors (GCCs, SIs, IT parks) and their credit ratings to identify which lender programmes accept them." },
            { n: "02", t: "Programme selection", d: "Disclosed (anchor-confirmed) or undisclosed (vendor-only) — we pick what protects your buyer relationship while maximising advance %." },
            { n: "03", t: "Onboarding (3–5 days)", d: "Single document checklist — GST, bank statements, KYC, sample POs / invoices. We coordinate with your CA so you don't lose a day." },
            { n: "04", t: "Live cycle", d: "Upload invoices → 24–48 hour disbursal → buyer pays directly to lender on due date. Repeat every billing cycle, indefinitely." },
          ].map((s, i) => (
            <li key={s.n} data-testid={`if-step-${i}`} className="card-soft p-6">
              <div className="font-outfit font-extrabold text-3xl text-brand-terracotta">{s.n}</div>
              <h3 className="mt-3 font-outfit font-bold text-lg text-brand-forest">{s.t}</h3>
              <p className="mt-2 text-sm text-brand-sage leading-relaxed">{s.d}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* WHO IT'S FOR */}
      <section className="bg-brand-sand/40 py-20 md:py-24">
        <div className="container-x">
          <span className="eyebrow">Who fits</span>
          <h2 className="mt-4 h-display text-3xl sm:text-4xl">Tier-2 IT &amp; tech vendors, ₹50L–₹10Cr turnover.</h2>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {[
              { t: "IT services & consulting", d: "Software development, devops, cybersecurity, data engineering — billing GCCs, captives or large SIs on monthly retainers or T&M contracts." },
              { t: "Product & SaaS sub-vendors", d: "Implementation partners, integration vendors, custom-build firms billing enterprise clients on milestone POs." },
              { t: "IT infrastructure & facility services", d: "Data-centre operations, NOC services, hardware AMC, facility management — recurring monthly invoices to GCCs and IT parks." },
            ].map((c, i) => (
              <div key={i} data-testid={`if-fit-${i}`} className="rounded-2xl border border-brand-sand bg-white p-6">
                <CheckCircle2 className="h-5 w-5 text-brand-terracotta" />
                <h3 className="mt-4 font-outfit font-bold text-lg text-brand-forest">{c.t}</h3>
                <p className="mt-2 text-sm text-brand-sage leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="container-x py-20 md:py-24">
        <div className="max-w-2xl">
          <span className="eyebrow">Frequently asked</span>
          <h2 className="mt-4 h-display text-3xl sm:text-4xl">Everything before your first call.</h2>
        </div>
        <Accordion type="single" collapsible className="mt-10 max-w-3xl">
          {FAQ.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} data-testid={`if-faq-${i}`} className="border-brand-sand">
              <AccordionTrigger className="text-left font-outfit font-semibold text-brand-forest text-base hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-brand-sage leading-relaxed">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* LEAD FORM */}
      <section id="lead" className="container-x pb-24 scroll-mt-28">
        <div className="rounded-3xl bg-brand-forest text-brand-cream p-8 md:p-12 grid md:grid-cols-12 gap-10 relative overflow-hidden">
          <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-brand-terracotta/20 blur-2xl" />
          <div className="absolute -left-10 bottom-0 h-48 w-48 rounded-full bg-brand-gold/20 blur-2xl" />

          <div className="md:col-span-6 relative">
            <span className="eyebrow text-brand-gold">Talk to a specialist</span>
            <h2 className="mt-4 font-outfit font-extrabold text-3xl sm:text-4xl tracking-tight">
              Get a tailored invoice-financing programme.
            </h2>
            <p className="mt-4 text-brand-cream/75 leading-relaxed">
              Share your numbers — Raj will personally reach out with a shortlist of
              best-fit lenders, indicative pricing on your anchor and the next steps.
              One business day, max.
            </p>

            <div className="mt-8 space-y-3 text-sm">
              <a href={`tel:${BRAND.phoneE164}`} className="flex items-center gap-3 text-brand-cream/85 hover:text-brand-gold">
                <Phone className="h-4 w-4 text-brand-gold" /> {BRAND.phone}
              </a>
              <a
                href={`https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent("Hi Raj, I'd like to discuss invoice financing for my IT vendor business.")}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 text-brand-cream/85 hover:text-brand-gold"
              >
                <MessageCircle className="h-4 w-4 text-brand-gold" /> WhatsApp Raj
              </a>
            </div>
          </div>

          <div className="md:col-span-6 relative">
            <div className="card-soft p-6 md:p-7 bg-white text-brand-forest">
              {done ? (
                <div data-testid="if-success" className="text-center py-6">
                  <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-brand-terracotta/10 text-brand-terracotta">
                    <CheckCircle2 className="h-6 w-6" />
                  </div>
                  <h3 className="mt-4 font-outfit font-bold text-xl">Got it. Raj will call you.</h3>
                  <p className="mt-3 text-sm text-brand-sage">
                    Expect a call from {BRAND.phone} within one business day.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="if-form" className="space-y-4">
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-brand-terracotta font-semibold">Free Programme Review</div>
                    <h3 className="mt-1 font-outfit font-bold text-2xl">2-min call. No spam.</h3>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="if-name" className="text-sm font-medium">Full Name</Label>
                      <Input id="if-name" data-testid="if-name" value={form.name} onChange={set("name")} placeholder="Your name" className="mt-1.5" />
                    </div>
                    <div>
                      <Label htmlFor="if-phone" className="text-sm font-medium">Phone</Label>
                      <Input id="if-phone" data-testid="if-phone" value={form.phone} onChange={set("phone")} placeholder="10-digit mobile" className="mt-1.5" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="if-email" className="text-sm font-medium">Email</Label>
                    <Input id="if-email" data-testid="if-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className="mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="if-company" className="text-sm font-medium">Company (optional)</Label>
                    <Input id="if-company" data-testid="if-company" value={form.company} onChange={set("company")} placeholder="Company name" className="mt-1.5" />
                  </div>
                  <button type="submit" disabled={submitting} data-testid="if-submit" className="btn-primary w-full text-base disabled:opacity-60">
                    {submitting ? (<><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</>) : (<>Get my programme review <ArrowRight className="h-4 w-4" /></>)}
                  </button>
                  <p className="text-[11px] text-brand-sage text-center">
                    Your details are used only to reach back about this inquiry.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Cross-link */}
        <div className="mt-10 text-center">
          <Link to="/business-owners" className="btn-ghost">
            Looking at general working-capital options? See the Business Owners page <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}

function ResultBox({ icon, label, value, testid }) {
  return (
    <div className="rounded-xl bg-brand-cream/5 border border-brand-cream/10 p-3" data-testid={testid}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-brand-cream/60">
        <span className="text-brand-gold">{icon}</span> {label}
      </div>
      <div className="mt-1 font-outfit font-semibold text-base text-brand-cream">{value}</div>
    </div>
  );
}
